// Discover: browsable directories of community content. Deliberately built as
// a section registry — each section owns its own search/filter state and
// queries, and new discover sections (people, routes…) plug in by adding an
// entry to SECTIONS below.
import { useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
  type QueryKey,
} from "@tanstack/react-query";
import { Link, useSearchParams } from "wouter";
import { CalendarDays, Lock, UsersRound } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import {
  DISCOVER_CLUBS,
  DISCOVER_EVENTS,
  MY_CLUB_MEMBERSHIPS,
  MY_EVENT_PARTICIPATIONS,
  type DiscoverClubNode,
  type DiscoverClubsResult,
  type DiscoverEventNode,
  type DiscoverEventsResult,
  type MyClubMembershipsResult,
  type MyEventParticipationsResult,
} from "../lib/queries";
import { useAuth } from "../lib/auth";
import { ACTIVITIES, ACTIVITY_ICONS, formatDateRange, type Activity } from "../lib/format";
import { useT, type MessageKey } from "../lib/i18n";
import { Avatar } from "../components/Avatar";
import { ActivityList, Chip, EmptyState, ErrorBox, Spinner, TextInput } from "../components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const PAGE_SIZE = 20;

// New discover sections plug in here: add a key, a label message and a
// component; the chip switcher and ?tab= handling pick it up automatically.
const SECTIONS: { key: string; labelKey: MessageKey; component: ComponentType }[] = [
  { key: "groups", labelKey: "discover.groups", component: GroupsSection },
  { key: "events", labelKey: "discover.events", component: EventsSection },
];

export function DiscoverPage() {
  const { t } = useT();
  const [params, setParams] = useSearchParams();

  const tabParam = params.get("tab");
  const section = SECTIONS.find((s) => s.key === tabParam) ?? SECTIONS[0]!;
  const setTab = (key: string) => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (key === SECTIONS[0]!.key) p.delete("tab");
        else p.set("tab", key);
        return p;
      },
      { replace: true },
    );
  };

  const Active = section.component;

  return (
    <div className="mx-auto max-w-2xl p-4">
      <h1 className="mb-4 text-2xl font-semibold tracking-tight text-foreground">
        {t("discover.title")}
      </h1>

      <div className="mb-4 inline-flex gap-1 rounded-xl bg-muted p-1" role="tablist" aria-label={t("nav.discover")}>
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={s.key === section.key}
            onClick={() => setTab(s.key)}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
              s.key === section.key
                ? "bg-card text-foreground shadow-card"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t(s.labelKey)}
          </button>
        ))}
      </div>

      <Active />
    </div>
  );
}

function useDebounced(value: string, delayMs = 300): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Infinite-scroll sentinel, same reasoning as FeedPage: re-observe when a
 * page lands and the sentinel is still visible, and never observe over
 * placeholder data (its offsets belong to the previous query key).
 */
function useLoadMoreSentinel(opts: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isPlaceholderData: boolean;
  fetchNextPage: () => void;
  count: number;
}) {
  const { hasNextPage, isFetchingNextPage, isPlaceholderData, fetchNextPage, count } = opts;
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage || isFetchingNextPage || isPlaceholderData) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) fetchNextPage();
      },
      { rootMargin: "400px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, isPlaceholderData, fetchNextPage, count]);
  return sentinelRef;
}

function ActivityChips({ selected, onToggle }: { selected: Activity[]; onToggle: (a: Activity) => void }) {
  const { labelizeT } = useT();
  return (
    <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
      {ACTIVITIES.map((a) => {
        const Icon = ACTIVITY_ICONS[a];
        return (
          <Chip key={a} active={selected.includes(a)} onClick={() => onToggle(a)}>
            <span className="inline-flex items-center gap-1.5">
              <Icon aria-hidden className="size-3.5" />
              {labelizeT(a)}
            </span>
          </Chip>
        );
      })}
    </div>
  );
}

/** Horizontal strip of chips linking to the viewer's own groups/events. */
function MineStrip({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      <div className="flex gap-2 overflow-x-auto pb-1">{children}</div>
    </div>
  );
}

function MineChip({ href, label, invited }: { href: string; label: string; invited?: boolean }) {
  const { t } = useT();
  return (
    <Link
      href={href}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
        invited
          ? "border-primary/20 bg-primary/5 text-primary hover:bg-primary/10"
          : "border-border bg-card text-foreground hover:bg-accent hover:text-accent-foreground"
      }`}
    >
      {label}
      {invited && (
        <Badge className="text-[10px] font-bold uppercase tracking-wide">{t("discover.invited")}</Badge>
      )}
    </Link>
  );
}

function LoadingMore() {
  const { t } = useT();
  return (
    <div className="mt-4 flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
      <span className="size-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
      {t("feed.loadingMore")}
    </div>
  );
}

// --- groups ---

function GroupsSection() {
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim());
  const [activities, setActivities] = useState<Activity[]>([]);

  // Shared query key with GroupPage / the outing share control, so the strip
  // is usually already cached.
  const memberships = useQuery({
    queryKey: ["my-club-memberships", personId],
    enabled: !!personId,
    queryFn: () => gqlRequest<MyClubMembershipsResult>(MY_CLUB_MEMBERSHIPS, { personId }),
  });
  const myRows = memberships.data?.clubMembers.nodes ?? [];
  const activeRows = myRows.filter((r) => r.status === "ACTIVE");
  // An INVITED row's club is null for HIDDEN clubs; the group page renders the
  // accept/decline card either way.
  const invitedRows = myRows.filter((r) => r.status === "INVITED");

  const filter: Record<string, unknown> = {};
  if (q) filter.name = { likeInsensitive: `%${q}%` };
  if (activities.length) filter.activities = { overlaps: activities };

  const { data, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage, isPlaceholderData } = useInfiniteQuery<
    DiscoverClubsResult,
    Error,
    InfiniteData<DiscoverClubsResult>,
    QueryKey,
    number
  >({
    queryKey: ["discover-clubs", q, activities],
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.clubs.nodes.length, 0);
      return loaded < lastPage.clubs.totalCount ? loaded : undefined;
    },
    queryFn: ({ pageParam }) =>
      gqlRequest<DiscoverClubsResult>(DISCOVER_CLUBS, {
        filter: Object.keys(filter).length ? filter : null,
        first: PAGE_SIZE,
        offset: pageParam,
      }),
  });

  const clubs = data?.pages.flatMap((p) => p.clubs.nodes) ?? [];
  const sentinelRef = useLoadMoreSentinel({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    isPlaceholderData,
    fetchNextPage,
    count: clubs.length,
  });

  return (
    <div>
      {personId && (activeRows.length > 0 || invitedRows.length > 0) && (
        <MineStrip title={t("discover.yourGroups")}>
          {invitedRows.map((r) => (
            <MineChip key={r.id} href={`/group/${r.clubId}`} label={r.club?.name ?? t("discover.hiddenGroup")} invited />
          ))}
          {activeRows.map((r) => (
            <MineChip key={r.id} href={`/group/${r.clubId}`} label={r.club?.name ?? t("discover.hiddenGroup")} />
          ))}
        </MineStrip>
      )}

      <div className="mb-3 flex items-center gap-2">
        <TextInput
          type="search"
          value={search}
          placeholder={t("discover.searchGroups")}
          onChange={(e) => setSearch(e.target.value)}
        />
        {/* /group/new is LoginGate'd, so anonymous visitors get the login card there. */}
        <Button asChild className="shrink-0">
          <Link href="/group/new">{t("discover.newGroup")}</Link>
        </Button>
      </div>
      <ActivityChips
        selected={activities}
        onToggle={(a) => setActivities((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]))}
      />

      {isLoading && <Spinner />}
      {error != null && <ErrorBox error={error} />}
      {data && clubs.length === 0 && <EmptyState>{t("discover.noGroups")}</EmptyState>}

      <div className="space-y-3">
        {clubs.map((c) => (
          <GroupCard key={c.id} club={c} />
        ))}
      </div>

      {isFetchingNextPage && <LoadingMore />}
      {hasNextPage && <div ref={sentinelRef} aria-hidden className="h-px" />}
    </div>
  );

  function GroupCard({ club }: { club: DiscoverClubNode }) {
    const memberCount = club.clubMembersByClubId.totalCount;
    return (
      <Link
        href={`/group/${club.id}`}
        className="block rounded-xl border bg-card p-4 text-card-foreground shadow-card transition-shadow hover:shadow-card-hover"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Avatar person={{ displayName: club.name, avatarKey: club.avatarKey }} size="sm" />
          <span className="font-semibold text-foreground">{club.name}</span>
          {club.visibility === "PRIVATE" && (
            <Lock aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
          )}
          <Badge variant="secondary" className="ml-auto">
            {labelizeT(club.joinPolicy)}
          </Badge>
        </div>
        <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <UsersRound aria-hidden className="size-3.5" />
            {t(memberCount === 1 ? "group.membersOne" : "group.membersOther", { count: memberCount })}
          </span>
          {club.activities && club.activities.length > 0 && <ActivityList activities={club.activities} />}
        </p>
        {club.description && (
          <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{club.description}</p>
        )}
      </Link>
    );
  }
}

// --- events ---

function EventsSection() {
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  // Captured once per mount so the query key doesn't churn every render.
  const [from] = useState(() => new Date().toISOString());
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim());
  const [activities, setActivities] = useState<Activity[]>([]);
  const [myGroupsOnly, setMyGroupsOnly] = useState(false);

  const memberships = useQuery({
    queryKey: ["my-club-memberships", personId],
    enabled: !!personId,
    queryFn: () => gqlRequest<MyClubMembershipsResult>(MY_CLUB_MEMBERSHIPS, { personId }),
  });
  const myClubIds = memberships.data?.clubMembers.nodes.filter((r) => r.status === "ACTIVE").map((r) => r.clubId);

  const participations = useQuery({
    queryKey: ["my-event-participations", personId],
    enabled: !!personId,
    queryFn: () => gqlRequest<MyEventParticipationsResult>(MY_EVENT_PARTICIPATIONS, { personId }),
  });
  const myParticipations = participations.data?.eventParticipants.nodes ?? [];
  const invitedRows = myParticipations.filter((r) => r.status === "INVITED");
  const goingRows = myParticipations.filter(
    (r) => r.status === "CONFIRMED" && r.event != null && r.event.startsAt >= from,
  );

  const groupToggleActive = myGroupsOnly && personId != null;
  const filter: Record<string, unknown> = { startsAt: { greaterThanOrEqualTo: from } };
  if (q) filter.title = { likeInsensitive: `%${q}%` };
  if (activities.length) filter.activities = { overlaps: activities };
  if (groupToggleActive && myClubIds) filter.clubId = { in: myClubIds };

  // Same shape as FeedPage's empty-followees handling: don't fetch with an
  // unresolved (or empty) club list under the toggle.
  const noGroups = groupToggleActive && myClubIds != null && myClubIds.length === 0;

  const { data, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage, isPlaceholderData } = useInfiniteQuery<
    DiscoverEventsResult,
    Error,
    InfiniteData<DiscoverEventsResult>,
    QueryKey,
    number
  >({
    queryKey: ["discover-events", from, q, activities, groupToggleActive ? myClubIds : null],
    placeholderData: keepPreviousData,
    enabled: !groupToggleActive || (myClubIds != null && myClubIds.length > 0),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.events.nodes.length, 0);
      return loaded < lastPage.events.totalCount ? loaded : undefined;
    },
    queryFn: ({ pageParam }) =>
      gqlRequest<DiscoverEventsResult>(DISCOVER_EVENTS, { filter, first: PAGE_SIZE, offset: pageParam }),
  });

  const showCards = !noGroups && (!groupToggleActive || myClubIds != null);
  const events = showCards ? (data?.pages.flatMap((p) => p.events.nodes) ?? []) : [];
  const sentinelRef = useLoadMoreSentinel({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    isPlaceholderData,
    fetchNextPage,
    count: events.length,
  });

  return (
    <div>
      {personId && (goingRows.length > 0 || invitedRows.length > 0) && (
        <MineStrip title={t("discover.yourEvents")}>
          {invitedRows.map((r) => (
            <MineChip key={r.id} href={`/event/${r.eventId}`} label={r.event?.title ?? t("discover.hiddenEvent")} invited />
          ))}
          {goingRows.map((r) => (
            <MineChip key={r.id} href={`/event/${r.eventId}`} label={r.event?.title ?? t("discover.hiddenEvent")} />
          ))}
        </MineStrip>
      )}

      <div className="mb-3 flex items-center gap-2">
        <TextInput
          type="search"
          value={search}
          placeholder={t("discover.searchEvents")}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Button asChild className="shrink-0">
          <Link href="/event/new">{t("discover.newEvent")}</Link>
        </Button>
      </div>
      <ActivityChips
        selected={activities}
        onToggle={(a) => setActivities((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]))}
      />
      {personId != null && (
        <div className="mb-4 -mt-1">
          <Chip active={myGroupsOnly} onClick={() => setMyGroupsOnly((v) => !v)}>
            {t("discover.myGroupsOnly")}
          </Chip>
        </div>
      )}

      {(isLoading || (groupToggleActive && memberships.isLoading)) && <Spinner />}
      {error != null && <ErrorBox error={error} />}
      {noGroups && <EmptyState>{t("discover.notInAnyGroup")}</EmptyState>}
      {showCards && data && events.length === 0 && <EmptyState>{t("discover.noEvents")}</EmptyState>}

      <div className="space-y-3">
        {events.map((e) => (
          <EventCard key={e.id} event={e} />
        ))}
      </div>

      {isFetchingNextPage && <LoadingMore />}
      {showCards && hasNextPage && <div ref={sentinelRef} aria-hidden className="h-px" />}
    </div>
  );

  function EventCard({ event }: { event: DiscoverEventNode }) {
    return (
      <Link
        href={`/event/${event.id}`}
        className="block rounded-xl border bg-card p-4 text-card-foreground shadow-card transition-shadow hover:shadow-card-hover"
      >
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
          <CalendarDays aria-hidden className="size-3.5" />
          {formatDateRange(event.startsAt, event.endsAt)}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="font-semibold text-foreground">{event.title}</span>
          <Badge variant="secondary" className="ml-auto">
            {labelizeT(event.joinPolicy)}
          </Badge>
        </div>
        <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          {event.club && (
            <Badge variant="outline" className="border-primary/20 bg-primary/5 text-primary">
              <UsersRound aria-hidden className="size-3" />
              {event.club.name}
            </Badge>
          )}
          {event.activities && event.activities.length > 0 && <ActivityList activities={event.activities} />}
        </p>
      </Link>
    );
  }
}
